import { useRef, useEffect, useState, useMemo } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface UseScrollRevealOptions {
  threshold?: number;
  delay?: number;
}

export function useScrollReveal({ threshold = 0.15, delay = 0 }: UseScrollRevealOptions = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el || reduced) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, reduced]);

  const className = reduced ? "" : visible ? "scroll-reveal scroll-reveal--visible" : "scroll-reveal";
  const style = useMemo(
    () => (!reduced && delay > 0 ? { animationDelay: `${delay}ms` } : undefined),
    [reduced, delay],
  );

  return { ref, className, style };
}
